import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { of, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import * as moment from 'moment';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';

import { DomSanitizer, SafeHtml, SafeResourceUrl } from '@angular/platform-browser';
import { ColorPickerService } from 'ngx-color-picker';
import { IoService } from '../../../services/io/io.service';
import { ReportService } from '../../../services/report/report.service';
import { AuthorizationService } from '../../../services/authorization/authorization.service';
import { LocationService } from '../../../services/location/location.service';
import { TagPrintService } from '../../../services/tag-print/tag-print.service';
import { DateRangeSettings } from '../filter-date-range/date-config-interface';
import { AppComponent } from 'src/app/app.component';
import { DialogOption } from '../../shared/ui/dialog/dialog-options';
import { InventoryOptionsComponent } from '../inventory-options/inventory-options.component';
import { CorrectionDialogComponent } from '../correction-dialog/correction-dialog.component';
import { InventoryVendorPricingComponent } from '../inventory-vendor-pricing/inventory-vendor-pricing.component';
import { InventoryLocationPricingComponent } from '../inventory-location-pricing/inventory-location-pricing.component';

// Components launchable from a View action of type 'component'. The action's
// `component` value on the View document selects the entry here.
const ACTION_COMPONENTS: { [key: string]: any } = {
  'add-correction': CorrectionDialogComponent,
  'inventory-options': InventoryOptionsComponent,
  'inventory-vendor-pricing': InventoryVendorPricingComponent,
  'inventory-location-pricing': InventoryLocationPricingComponent,
};

// Built-in dialog for the Advanced Pricing button rendered under advancedPrice
// fields — not a View-doc action, but it opens through the same ?dialogAction
// mechanism so deep links and the browser Back button work.
const ADVANCED_PRICING_ACTION = {
  key: 'advancedPricing',
  type: 'component',
  component: 'inventory-location-pricing',
  width: '640px',
  height: 'auto',
};

@Component({
  selector: 'app-detail-view',
  templateUrl: './detail-view.component.html',
  styleUrls: ['./detail-view.component.scss']
})
export class DetailViewComponent implements OnInit, OnDestroy {

  documentId: string;
  viewKey: string;

  view: any;
  document: any;
  editValues: any = {};
  displayValues: { [path: string]: any } = {};
  colorDisplay: { [path: string]: string } = {};
  arrayInputValues: { [path: string]: string } = {};
  errors: string[] = [];
  saving = false;
  remoteLookupLoading: { [path: string]: boolean } = {};
  canUpdate = false;
  // view.<key>.update held somewhere — the raw permission. canUpdate is this AND
  // the server's per-record verdict (targetManageable), so a User record whose
  // group is outside the viewer's "Manage / Grant User Group" grid renders
  // read-only even though the viewer can edit other users.
  canUpdatePermission = false;
  targetManageable = true;
  isSelfDocument = false;
  // Gates the Advanced Pricing button on advancedPrice fields — the dialog's
  // getInventoryPricing endpoint requires account.inventory.detail.
  canAdvancedPricing = false;
  uploaderUrl: SafeResourceUrl = null;
  activeImageField: any = null;
  canUploadImages = false;
  dragOverField: any = null;
  uploading = false;
  permittedActions: any[] = [];
  // Actions with aboveSave: true on the View doc render before the Save button.
  permittedActionsAboveSave: any[] = [];
  executingAction = false;
  // Snapshot of editValues taken at load; compared against to detect unsaved edits.
  private editBaseline: string = null;
  // Component-action dialog state is URL-driven (?dialogAction=<key>) so the
  // browser Back button can close and reopen the dialog.
  private dialogActionKey: string = null;
  private activeComponentDialog: any = null;
  private queryParamsSub: Subscription;
  // Resolved server context for relational field types (childList/parentLink/moveItems)
  // and per-action record counts (e.g. View Products (13)).
  context: { [path: string]: any } = {};
  actionCounts: { [key: string]: number } = {};
  actionAllowed: { [key: string]: boolean } = {};
  // Selected move-target per moveItems field path.
  moveTargets: { [path: string]: string } = {};
  movingItems = false;
  activeFields: any[] = [];
  showFieldEditor = false;
  editorFields: { field: any, active: boolean }[] = [];
  savingSettings = false;
  dateTimeSettings: { [path: string]: DateRangeSettings } = {};
  calculatedValues: { [name: string]: number | null } = {};
  rtState: { [path: string]: any } = {};
  rtDropdown: { [path: string]: string } = {};
  rtLinkInput: { [path: string]: { active: boolean, url: string } } = {};
  rtFontSizes = [
    { label: 'Small', value: '2' },
    { label: 'Normal', value: '3' },
    { label: 'Large', value: '5' },
    { label: 'Huge', value: '7' },
  ];
  rtColors = [
    '#000000', '#434343', '#e53935', '#d81b60',
    '#1e88e5', '#1565c0', '#43a047', '#2e7d32',
    '#fb8c00', '#f4511e', '#8e24aa', '#5e35b1',
  ];
  activeLocation: any;
  states = [
    { name: 'Alabama', key: 'AL' }, { name: 'Alaska', key: 'AK' }, { name: 'Arizona', key: 'AZ' },
    { name: 'Arkansas', key: 'AR' }, { name: 'California', key: 'CA' }, { name: 'Colorado', key: 'CO' },
    { name: 'Connecticut', key: 'CT' }, { name: 'Delaware', key: 'DE' }, { name: 'Florida', key: 'FL' },
    { name: 'Georgia', key: 'GA' }, { name: 'Hawaii', key: 'HI' }, { name: 'Idaho', key: 'ID' },
    { name: 'Illinois', key: 'IL' }, { name: 'Indiana', key: 'IN' }, { name: 'Iowa', key: 'IA' },
    { name: 'Kansas', key: 'KS' }, { name: 'Kentucky', key: 'KY' }, { name: 'Louisiana', key: 'LA' },
    { name: 'Maine', key: 'ME' }, { name: 'Maryland', key: 'MD' }, { name: 'Massachusetts', key: 'MA' },
    { name: 'Michigan', key: 'MI' }, { name: 'Minnesota', key: 'MN' }, { name: 'Mississippi', key: 'MS' },
    { name: 'Missouri', key: 'MO' }, { name: 'Montana', key: 'MT' }, { name: 'Nebraska', key: 'NE' },
    { name: 'Nevada', key: 'NV' }, { name: 'New Hampshire', key: 'NH' }, { name: 'New Jersey', key: 'NJ' },
    { name: 'New Mexico', key: 'NM' }, { name: 'New York', key: 'NY' }, { name: 'North Carolina', key: 'NC' },
    { name: 'North Dakota', key: 'ND' }, { name: 'Ohio', key: 'OH' }, { name: 'Oklahoma', key: 'OK' },
    { name: 'Oregon', key: 'OR' }, { name: 'Pennsylvania', key: 'PA' }, { name: 'Rhode Island', key: 'RI' },
    { name: 'South Carolina', key: 'SC' }, { name: 'South Dakota', key: 'SD' }, { name: 'Tennessee', key: 'TN' },
    { name: 'Texas', key: 'TX' }, { name: 'Utah', key: 'UT' }, { name: 'Vermont', key: 'VT' },
    { name: 'Virginia', key: 'VA' }, { name: 'Washington', key: 'WA' }, { name: 'West Virginia', key: 'WV' },
    { name: 'Wisconsin', key: 'WI' }, { name: 'Wyoming', key: 'WY' },
  ];
  private permissionsSub: Subscription;
  private locationSub: Subscription;
  private dragLeaveTimer: any;
  private rtSavedSelections: { [path: string]: Range } = {};

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private ioService: IoService,
    private reportService: ReportService,
    private authorizationService: AuthorizationService,
    private locationService: LocationService,
    private tagPrintService: TagPrintService,
    private sanitizer: DomSanitizer,
    private colorPickerService: ColorPickerService,
  ) { }

  @HostListener('document:mousedown', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('.rt-toolbar') && !target.closest('.rt-link-bar')) {
      this.rtDropdown = {};
    }
  }

  @HostListener('window:message', ['$event'])
  onMessage(e: MessageEvent) {
    if (e.data && e.data.type === 'fileApiResponse' && this.activeImageField) {
      const url: string = e.data.url;
      const field = this.activeImageField;
      if (this.isArrayField(field)) {
        const arr: string[] = this.editValues[field.path] || [];
        if (arr.length < field.limit) {
          arr.push(url);
          this.editValues[field.path] = arr;
        }
      } else {
        this.editValues[field.path] = url;
      }
      this.activeImageField = null;
      this.initializeUploader();
    }
  }

  ngOnInit(): void {
    this.activeLocation = this.locationService.getActiveLocation();

    this.route.paramMap.pipe(
      switchMap(params => {
        this.viewKey = params.get('viewKey');
        this.documentId = params.get('documentId');
        return of({ viewKey: this.viewKey, documentId: this.documentId });
      })
    ).subscribe(params => {
      if (params.viewKey && params.documentId) {
        this.view = null;
        this.document = null;
        this.errors = [];
        this.initView();
      }
    });

    this.locationSub = this.locationService.activeLocationSetObservable().subscribe(location => {
      this.activeLocation = location;
      if (this.view && this.view.fields.some(f => f.locationSpecific)) {
        this.loadView();
      }
    });

    this.queryParamsSub = this.route.queryParamMap.subscribe(params => {
      this.dialogActionKey = params.get('dialogAction');
      this.syncComponentDialog();
    });
  }

  ngOnDestroy(): void {
    if (this.permissionsSub) {
      this.permissionsSub.unsubscribe();
    }
    if (this.locationSub) {
      this.locationSub.unsubscribe();
    }
    if (this.queryParamsSub) {
      this.queryParamsSub.unsubscribe();
    }
    if (this.activeComponentDialog) {
      this.activeComponentDialog.close({ fromNavigation: true });
    }
  }

  close() {
    this.reportService.closeDetailPanel(this.route, 3);
  }

  private initView() {
    if (this.permissionsSub) {
      this.permissionsSub.unsubscribe();
    }

    if (this.authorizationService.permissionsAreLoaded()) {
      this.canUpdatePermission = this.authorizationService.checkPermission('view.' + this.viewKey + '.update', 'any', false);
      this.canUpdate = this.canUpdatePermission;
      this.canUploadImages = this.authorizationService.checkPermission('uploadImages', 'any');
      this.canAdvancedPricing = this.authorizationService.checkPermission('account.inventory.detail', 'any');
      this.loadView();
    } else {
      this.permissionsSub = this.authorizationService.permissionsLoadedObservable().subscribe(loaded => {
        if (loaded) {
          this.canUpdatePermission = this.authorizationService.checkPermission('view.' + this.viewKey + '.update', 'any', false);
          this.canUpdate = this.canUpdatePermission;
          this.canUploadImages = this.authorizationService.checkPermission('uploadImages', 'any');
          this.canAdvancedPricing = this.authorizationService.checkPermission('account.inventory.detail', 'any');
          this.loadView();
          this.permissionsSub.unsubscribe();
        }
      });
    }
  }

  async loadView() {
    try {
      const result: any = await this.ioService.post('/view/getView', {
        viewKey: this.viewKey,
        documentId: this.documentId,
        locationId: this.activeLocation?._id || null,
        returnContext: true,
        ignoreAlias: true,
      });

      this.view = result.view;
      this.document = result.document;
      this.context = result.context || {};
      // Server verdict for User records (see getView): false → read-only, actions
      // hidden except a user's own Set PIN. Other collections never send it.
      this.targetManageable = this.context.activeUserCanManage !== false;
      this.isSelfDocument = this.context.isSelf === true;
      this.canUpdate = this.canUpdatePermission && this.targetManageable;
      this.actionCounts = result.actionCounts || {};
      this.actionAllowed = result.actionAllowed || {};
      this.moveTargets = {};
      this.loadActions();
      this.resolveActiveFields(result.userActiveFields);
      this.dateTimeSettings = {};
      this.displayValues = {};
      this.colorDisplay = {};
      this.arrayInputValues = {};

      const resolvedDisplayValues = result.displayValues || {};

      for (const field of this.view.fields) {
        let value = this.document[field.path];

        if ((field.type === 'currency' || field.type === 'advancedPrice') && value != null) {
          // format 'inverse': stored negated (e.g. a credit item's price is
          // -50 cents) but shown/edited as a positive amount.
          value = ((field.format === 'inverse' ? -value : value) / 100).toFixed(2);
        } else if (field.type === 'number' && field.format === 'percentage' && value != null) {
          // Stored as a decimal (0.05), edited as a whole percent (5)
          value = parseFloat((value * 100).toFixed(4));
        } else if (field.type === 'boolean') {
          // Documents may lack the field entirely; the API rejects non-boolean
          // values. A mapped boolean (trueValue/falseValue) is on only when
          // the stored value is exactly its trueValue.
          value = field.trueValue !== undefined ? value === field.trueValue : !!value;
        } else if (field.type === 'colorPicker') {
          // Stored as {r, g, b, a}; the swatch/picker binds an rgba() string
          this.colorDisplay[field.path] = value != null
            ? 'rgba(' + value.r + ',' + value.g + ',' + value.b + ',0.5)'
            : 'rgba(0, 0, 0, 0)';
        } else if (field.type === 'dateTime') {
          const m = value != null ? moment(value) : moment();
          const settings = new DateRangeSettings();
          settings.dateConfig.singleDate = true;
          settings.dateConfig.includeTime = true;
          settings.dateConfig.autoApply = false;
          settings.dateConfig.showRanges = false;
          settings.dateConfig.ranges = null;
          settings.dateConfig.opens = 'left';
          settings.dateRange.startDate = m;
          settings.dateRange.endDate = m;
          this.dateTimeSettings[field.path] = settings;
        } else if (field.type === 'sourceCollection') {
          if (field.limit) {
            this.displayValues[field.path] = resolvedDisplayValues[field.path] || [];
          } else {
            this.displayValues[field.path] = resolvedDisplayValues[field.path] || '';
          }
        }

        if (field.type === 'address') {
          if (field.limit) {
            value = Array.isArray(value) ? value : (value != null ? [value] : []);
          } else {
            value = value || {};
          }
        }

        if (field.limit && !Array.isArray(value)) {
          value = value != null ? [value] : [];
        }

        this.editValues[field.path] = value;
      }

      const hasEditableImageFields = this.view.fields.some(f => f.type === 'image' && this.isFieldEditable(f));
      if (hasEditableImageFields && this.canUploadImages) {
        this.initializeUploader();
      }
      setTimeout(() => this.initRichTextEditors(), 0);
      this.editBaseline = JSON.stringify(this.editValues);
      this.syncComponentDialog();
    } catch (e) {
      this.errors = [e?.error?.msg || 'Failed to load view.'];
    }
  }

  // Keeps the component-action dialog in step with the ?dialogAction query param:
  // param present → open the matching action's dialog (covers deep links and the
  // browser Forward/Back buttons); param gone while open → close it.
  private syncComponentDialog() {
    if (this.dialogActionKey && !this.activeComponentDialog && this.view) {
      // Query params can change before the route param subscription has loaded
      // the new document (e.g. browser Back across records) — wait for the
      // loadView that follows, which calls this again with fresh data.
      if (this.documentId !== this.route.snapshot.paramMap.get('documentId')) {
        return;
      }
      let action = [...this.permittedActionsAboveSave, ...this.permittedActions]
        .find(a => a.type === 'component' && a.key === this.dialogActionKey);
      if (!action && this.dialogActionKey === ADVANCED_PRICING_ACTION.key && this.canAdvancedPricing
          && this.view.fields.some(f => f.type === 'advancedPrice')) {
        action = ADVANCED_PRICING_ACTION;
      }
      if (action) {
        this.openComponentDialog(action);
      }
    } else if (!this.dialogActionKey && this.activeComponentDialog) {
      this.activeComponentDialog.close({ fromNavigation: true });
    }
  }

  private openComponentDialog(action: any) {
    const componentClass = ACTION_COMPONENTS[action.component];
    if (!componentClass) {
      this.errors = ['Component "' + action.component + '" is not registered.'];
      return;
    }
    const componentRef = AppComponent.dialog.open(componentClass, {
      width: action.width || '95vw',
      maxWidth: '95vw',
      height: action.height || '92vh',
      maxHeight: '92vh',
      disableClose: false,
      panelClass: 'opaque-dialog',
      data: {
        documentId: this.documentId,
        viewKey: this.viewKey,
        document: this.document,
      },
    });
    this.activeComponentDialog = componentRef;
    componentRef.afterClosed().subscribe(async (result: any) => {
      this.activeComponentDialog = null;
      if (result && result.openItemId) {
        // Open the item in this same View. The ?dialogAction entry stays behind
        // in history, so the browser Back button restores this dialog.
        this.router.navigate(['..', result.openItemId], {
          relativeTo: this.route,
          queryParams: { dialogAction: null },
          queryParamsHandling: 'merge',
        });
        return;
      }
      if (!result || !result.fromNavigation) {
        // Closed by the user: drop the param without adding a history entry.
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { dialogAction: null },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        });
      }
      // Reload so values the dialog changed (e.g. calculated costs) show fresh.
      await this.loadView();
    });
  }

  // Opens the Advanced Pricing dialog for an advancedPrice field via the
  // ?dialogAction param so its open state lives in browser history.
  async openAdvancedPricing() {
    if (!await this.saveUnsavedChangesBeforeAction()) {
      return;
    }
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { dialogAction: ADVANCED_PRICING_ACTION.key },
      queryParamsHandling: 'merge',
    });
  }

  // True when any field edit hasn't been saved yet. dateTime edits live in
  // dateTimeSettings rather than editValues, so they aren't detected here.
  hasUnsavedChanges(): boolean {
    return this.editBaseline != null && JSON.stringify(this.editValues) !== this.editBaseline;
  }

  toggleBoolean(path: string) {
    this.editValues[path] = !this.editValues[path];
  }

  onColorChange(field: any, newColor: string) {
    this.colorDisplay[field.path] = newColor;
    const hsva = this.colorPickerService.stringToHsva(newColor, true);
    this.editValues[field.path] = this.colorPickerService.denormalizeRGBA(this.colorPickerService.hsvaToRgba(hsva));
  }

  onSourceSelectionChange(field: any, event: { value: string | null, display: string }) {
    this.editValues[field.path] = event.value;
    this.displayValues[field.path] = event.display;
  }

  async save(closeAfter = true): Promise<boolean> {
    if (this.saving) return false;
    this.saving = true;
    this.errors = [];

    const changes: any = {};
    const activePaths = new Set(this.activeFields.map(f => f.path));
    for (const field of this.view.fields) {
      if (this.isNavField(field)) continue;
      if (!field.editable) continue;
      if (field.type === 'calculated') continue;
      if (!activePaths.has(field.path)) continue;
      if (field.locationSpecific && (!this.activeLocation || !this.activeLocation._id)) continue;

      let value = this.editValues[field.path];

      if (field.limit) {
        let arrValue = value || [];
        if (field.type === 'address' && Array.isArray(arrValue)) {
          arrValue = arrValue.filter(a => a.streetAddress || a.streetAddressExt
            || a.addressLocality || a.addressRegion || a.postalCode);
        } else if (field.type === 'telephone' && Array.isArray(arrValue)) {
          const normalized: string[] = [];
          for (const v of arrValue) {
            const result = this.normalizeTelephone(v);
            if (result.error) {
              this.errors.push(`${field.name}: ${result.error}`);
            } else if (result.value) {
              normalized.push(result.value);
            }
          }
          arrValue = normalized;
        }
        changes[field.path] = arrValue;
        if (field.required && (!Array.isArray(arrValue) || arrValue.length === 0)) {
          this.errors.push(`${field.name} is required.`);
        }
        continue;
      }

      if ((field.type === 'currency' || field.type === 'advancedPrice') && value != null && value !== '') {
        value = Math.round(parseFloat(value) * 100);
        if (field.format === 'inverse' && !isNaN(value)) {
          // Inverse fields are entered as positive amounts; a negative entry
          // would store a positive value (e.g. a credit that CHARGES).
          if (value < 0) {
            this.errors.push(`${field.name} must be a positive amount.`);
            this.saving = false;
            return false;
          }
          value = -value;
        }
        if (isNaN(value)) {
          this.errors.push(`${field.name} must be a valid number.`);
          this.saving = false;
          return false;
        }
      } else if (field.type === 'number' && field.format === 'percentage' && value != null && value !== '') {
        value = parseFloat(value) / 100;
        if (isNaN(value)) {
          this.errors.push(`${field.name} must be a valid number.`);
          this.saving = false;
          return false;
        }
      } else if (field.type === 'telephone') {
        const result = this.normalizeTelephone(value);
        if (result.error) {
          this.errors.push(`${field.name}: ${result.error}`);
        }
        value = result.value;
      } else if (field.type === 'dateTime') {
        const settings = this.dateTimeSettings[field.path];
        if (settings && settings.dateRange.startDate) {
          value = settings.dateRange.startDate.toISOString();
        } else {
          value = null;
        }
      } else if (field.type === 'address' && !field.limit) {
        // Single address: strip if all fields are empty
        if (value && !value.streetAddress && !value.streetAddressExt
            && !value.addressLocality && !value.addressRegion && !value.postalCode) {
          value = null;
        }
      }

      if (field.type === 'string' && this.hasOptions(field)
          && value != null && value !== '' && field.options.indexOf(value) === -1) {
        this.errors.push(`${field.name} must be one of: ${field.options.join(', ')}.`);
      }

      if (field.required && (value == null || value === '')) {
        this.errors.push(`${field.name} is required.`);
      }

      changes[field.path] = value;
    }

    if (this.errors.length > 0) {
      this.saving = false;
      return false;
    }

    try {
      await this.postUpdate(changes, false, closeAfter);
      this.saving = false;
      return true;
    } catch (e) {
      if (e?.error?.errorKey === 'duplicateUser') {
        this.saving = false;
        this.promptDuplicateUser(e.error, changes);
      } else {
        this.errors = [e?.error?.msg || 'Failed to save.'];
        this.saving = false;
      }
      return false;
    }
  }

  private async postUpdate(changes: any, allowNameDuplicate: boolean, closeAfter = true) {
    await this.ioService.post('/view/updateView', {
      viewKey: this.viewKey,
      documentId: this.documentId,
      changes,
      locationId: this.activeLocation?._id || null,
      allowNameDuplicate,
    });

    this.reportService.loadReport(null);
    if (closeAfter) {
      this.close();
    } else {
      // Stay open (e.g. saving on the way into a component dialog): reload so
      // the document and dirty baseline reflect what was just written.
      await this.loadView();
    }
  }

  // The server matched an existing person by email, telephone, or exact name
  // (views over the User collection). Ask the operator whether this is the same
  // person or a genuinely new one. Name matches may be saved anyway;
  // email/telephone duplicates cannot.
  private promptDuplicateUser(duplicate: any, changes: any) {
    const matchedName = duplicate.matchedName || 'An existing record';
    const matchType = duplicate.matchType || 'information';

    if (duplicate.matchedUserType === 'customer') {
      // The match is a customer record — this view can't open it (its filters
      // exclude customers). Offer converting the customer to a user instead,
      // which keeps their purchase history.
      const options = matchType === 'name' ? ['Same Person', 'Save as New', 'Cancel'] : ['Same Person', 'Cancel'];
      AppComponent.showMessage(
        'Exists as Customer',
        `${matchedName} already exists as a customer with the same ${matchType}. Is this the same person? ` +
        'Choosing Same Person converts the customer to a user and keeps their purchase history.',
        options, true, options.length > 2 ? '760px' : '500px'
      ).afterClosed().subscribe(async (result: DialogOption) => {
        if (result?.value === 'Same Person' && duplicate.matchedUserId) {
          await this.convertCustomerToUser(duplicate.matchedUserId, changes);
        } else if (result?.value === 'Save as New') {
          await this.retrySaveAllowingNameDuplicate(changes);
        }
      });
      return;
    }

    const options = matchType === 'name'
      ? ['Open Existing', 'Save as New', 'Cancel']
      : ['Open Existing', 'Cancel'];

    AppComponent.showMessage(
      'Possible Duplicate',
      `${matchedName} already exists with the same ${matchType}. Is this the same person?`,
      options, true, options.length > 2 ? '760px' : '500px'
    ).afterClosed().subscribe(async (result: DialogOption) => {
      if (result?.value === 'Open Existing' && duplicate.matchedUserId) {
        this.navigateToRecord(duplicate.matchedUserId);
      } else if (result?.value === 'Save as New') {
        await this.retrySaveAllowingNameDuplicate(changes);
      }
    });
  }

  private async retrySaveAllowingNameDuplicate(changes: any) {
    this.saving = true;
    try {
      await this.postUpdate(changes, true);
    } catch (e) {
      this.errors = [e?.error?.msg || 'Failed to save.'];
    }
    this.saving = false;
  }

  // Convert the matched customer to a user, apply the operator's edits to the
  // converted record, and move there. Identity fields go through convertUserType
  // (updateUser semantics); view-only fields (e.g. custom data fields) are then
  // saved via updateView against the converted record, which updateUser would
  // silently drop. The current record is cleaned up server-side when it was a
  // blank stub from the add flow.
  private async convertCustomerToUser(matchedUserId: string, changes: any) {
    this.saving = true;
    try {
      const doc = this.document || {};
      const isBlankStub = !doc.firstName && !doc.lastName && !doc.email && !doc.telephone;

      const identityPaths = ['firstName', 'lastName', 'middleInitial', 'email', 'telephone', 'address'];
      const identityUpdate: any = {};
      const viewOnlyChanges: any = {};
      for (const path of Object.keys(changes)) {
        if (path === 'userGroupId') continue; // preserve converted user's membership + add-flow defaults
        if (identityPaths.indexOf(path) !== -1) {
          identityUpdate[path] = changes[path];
        } else {
          viewOnlyChanges[path] = changes[path];
        }
      }

      await this.ioService.post('/user/convertUserType', {
        userId: matchedUserId,
        stubUserId: isBlankStub ? this.documentId : null,
        user: identityUpdate,
        allowNameDuplicate: true,
      });

      if (Object.keys(viewOnlyChanges).length > 0) {
        await this.ioService.post('/view/updateView', {
          viewKey: this.viewKey,
          documentId: matchedUserId,
          changes: viewOnlyChanges,
          locationId: this.activeLocation?._id || null,
          allowNameDuplicate: true,
        });
      }

      AppComponent.showSnack('Customer converted to user.', 'OK', 3000);
      this.reportService.loadReport(null);
      this.navigateToRecord(matchedUserId);
    } catch (e) {
      this.errors = [e?.error?.msg || 'Failed to convert customer.'];
    }
    this.saving = false;
  }

  // A string field with a defined options list is edited via a dropdown limited
  // to those options (the value is still stored as a plain string).
  hasOptions(field: any): boolean {
    return !!field && Array.isArray(field.options) && field.options.length > 0;
  }

  isFieldEditable(field: any): boolean {
    if (!this.canUpdate || !field.editable) return false;
    if (field.locationSpecific && (!this.activeLocation || !this.activeLocation._id)) return false;
    return true;
  }

  isArrayField(field: any): boolean {
    return field.limit != null && field.limit > 0;
  }

  // Authorization-scoped sourceCollection fields (source.authorizationKeyPrefix)
  // arrive with authorizedValues: the ids the active user may assign/unassign.
  // Values outside that set render as locked chips — visible but not removable
  // (the server rejects such changes regardless).
  canRemoveSourceValue(field: any, index: number): boolean {
    if (!field || !Array.isArray(field.authorizedValues)) return true;
    const value = (this.editValues[field.path] || [])[index];
    return value != null && field.authorizedValues.indexOf(String(value)) !== -1;
  }

  isFieldVisible(field: any): boolean {
    // Relational field types are driven by resolved server context, not editValues.
    if (field.type === 'parentLink') {
      return !!this.context[field.path];
    }
    if (field.type === 'moveItems') {
      return (this.context[field.path]?.length || 0) > 0;
    }
    if (field.type === 'childList') {
      return (this.context[field.path]?.length || 0) > 0 || !!field.addRoute;
    }
    if (field.type === 'notes') {
      // getView inlines the notes into context; null/absent = no note.<type>.view
      // permission (or an older server) — hide the section entirely.
      return Array.isArray(this.context[field.path]);
    }
    if (field.type === 'detailLink') {
      return !!this.document?.[field.path];
    }
    if (field.type === 'corrections') {
      // The field renders the corrections trail; the corrects-backlink renders
      // as a banner at the top of the panel (correctionBanner getter).
      return !!(this.context[field.path]?.corrections?.length);
    }
    if (!field.conditionalPath) return true;
    // Live edit value first (so a section follows its select before save); a
    // path that is not itself a view field (e.g. a server-computed flag) falls
    // back to the loaded document.
    const val = this.editValues[field.conditionalPath] !== undefined
      ? this.editValues[field.conditionalPath] : this.document?.[field.conditionalPath];
    if (field.conditionalValue !== undefined) {
      // Show only when the condition path equals one of the given value(s).
      const wanted = Array.isArray(field.conditionalValue) ? field.conditionalValue : [field.conditionalValue];
      return wanted.some((w) => w === val || String(w) === String(val));
    }
    if (val === undefined || val === null || val === false || val === '') return false;
    if (Array.isArray(val) && val.length === 0) return false;
    return true;
  }

  // Relational/action field types render their own header + controls and hold no
  // editable value, so the generic label and save logic skip them.
  isNavField(field: any): boolean {
    return field.type === 'childList' || field.type === 'parentLink' || field.type === 'moveItems'
      || field.type === 'notes' || field.type === 'detailLink' || field.type === 'corrections';
  }

  // Navigate to a related record (parent/child), preserving ?variant=. A field
  // whose viewKey names another view (a scheduled report's runs) opens the
  // record in that view; otherwise the record is another document of this one.
  navigateToRecord(recordId: string, viewKey?: string) {
    if (!recordId) return;
    if (viewKey && viewKey !== this.viewKey) {
      this.reportService.navigateWithinReport(['../../', viewKey, String(recordId)], this.route);
      return;
    }
    this.reportService.navigateWithinReport(['../', recordId], this.route);
  }

  // A sourceCollection field with linkViewKey toggles to the referenced record
  // in that OTHER view (e.g. a vendor product's Replenishes → retail-product).
  // The panel swaps documents, so unsaved edits get the Save & Continue prompt
  // first; browser Back returns here.
  async openLinkedRecord(field: any) {
    const recordId = this.editValues[field.path];
    if (!recordId || !field.linkViewKey) return;
    if (!await this.saveUnsavedChangesBeforeAction()) return;
    this.reportService.navigateWithinReport(['../../', field.linkViewKey, String(recordId)], this.route);
  }

  // A correction transaction announces itself with a banner above the fields —
  // sourced from whichever active 'corrections' field resolved a backlink.
  get correctionBanner(): any {
    for (const field of this.activeFields || []) {
      if (field.type === 'corrections' && this.context[field.path]?.correctionFor) {
        return this.context[field.path];
      }
    }
    return null;
  }

  // detailLink fields display a cached value (e.g. purchaseKey) and navigate to a
  // legacy detail route on click. routeOverrides can retarget by document value —
  // first entry whose when.path value is in when.in wins (e.g. transfer-type
  // purchases open in the transfers module instead of the purchase detail).
  detailLinkId(field: any): any {
    return this.document?.[field.localPath || field.path];
  }

  private detailLinkTarget(field: any): any {
    for (const override of (field.routeOverrides || [])) {
      const when = override.when;
      if (when && Array.isArray(when.in) && when.in.indexOf(this.document?.[when.path]) !== -1) {
        return override;
      }
    }
    return field;
  }

  openDetailLink(field: any) {
    const recordId = this.detailLinkId(field);
    if (!recordId) return;
    const target = this.detailLinkTarget(field);
    if (target.module) {
      // Cross-module target (e.g. /transfers/:id) — variant param does not apply.
      this.router.navigate(['/' + target.module, String(recordId)]);
    } else if (target.route) {
      // Legacy detail routes (customer/:id, purchase/:id, ...) are siblings of
      // details/:viewKey/:documentId — climb all three segments to the report root.
      this.reportService.navigateWithinReport(['../../../' + target.route + '/' + String(recordId)], this.route);
    }
  }

  // Gate a config entry on the document: include only when document[whenPath] is
  // truthy and document[unlessPath] is falsy. Absent conditions always pass.
  private passesDocCondition(spec: any): boolean {
    if (spec.whenPath != null && !this.document?.[spec.whenPath]) return false;
    if (spec.unlessPath != null && this.document?.[spec.unlessPath]) return false;
    return true;
  }

  // Resolve a config map's `$path` tokens against the current document.
  private resolveDataRefs(data: any): any {
    const resolved: any = {};
    for (const key of Object.keys(data || {})) {
      const val = data[key];
      resolved[key] = (typeof val === 'string' && val.startsWith('$')) ? this.document[val.substring(1)] : val;
    }
    return resolved;
  }

  // Whether the Add button shows, gated by the field's addWhenPath/addUnlessPath
  // against the document (e.g. hide "Add Child Category" on a subcategory: addUnlessPath 'parentId').
  canAddChild(field: any): boolean {
    return this.passesDocCondition({ whenPath: field.addWhenPath, unlessPath: field.addUnlessPath });
  }

  async addChild(field: any) {
    if (this.executingAction || !field.addRoute) return;
    this.executingAction = true;
    this.errors = [];
    try {
      const data = this.resolveDataRefs(field.addData);
      // Optional suffix appended to the child's name (e.g. "Wine - Subcategory").
      if (field.addNameSuffix && data.name != null) {
        data.name = `${data.name}${field.addNameSuffix}`;
      }
      const newRecord: any = await this.ioService.post(field.addRoute, data);
      if (newRecord?._id) {
        this.navigateToRecord(newRecord._id);
      } else {
        await this.loadView();
      }
    } catch (e) {
      this.errors = [e?.error?.msg || 'Failed to add.'];
    }
    this.executingAction = false;
  }

  async doMoveItems(field: any) {
    const target = this.moveTargets[field.path];
    if (this.movingItems || !target || !field.route) return;
    this.movingItems = true;
    this.errors = [];
    try {
      const payload: any = this.resolveDataRefs(field.data);
      payload[field.fromKey || 'fromGroupId'] = this.documentId;
      payload[field.toKey || 'toGroupId'] = target;
      await this.ioService.post(field.route, payload);
      AppComponent.showSnack('Products moved.', 'OK', 3000);
      this.moveTargets[field.path] = '';
      this.reportService.loadReport(null);
      this.close();
    } catch (e) {
      this.errors = [e?.error?.msg || 'Failed to move products.'];
      this.movingItems = false;
    }
  }

  addAddress(field: any) {
    const arr: any[] = this.editValues[field.path] || [];
    if (field.limit && arr.length >= field.limit) return;
    arr.push({});
    this.editValues[field.path] = arr;
  }

  removeAddress(field: any, index: number) {
    const arr: any[] = this.editValues[field.path];
    if (arr) { arr.splice(index, 1); }
  }

  formatAddress(addr: any): string {
    if (!addr) return '';
    const parts = [addr.streetAddress, addr.streetAddressExt].filter(p => p);
    const cityLine = [addr.addressLocality, addr.addressRegion].filter(p => p).join(', ');
    if (cityLine) parts.push(cityLine + (addr.postalCode ? ' ' + addr.postalCode : ''));
    else if (addr.postalCode) parts.push(addr.postalCode);
    return parts.join(', ');
  }

  addStringValue(field: any, value: string) {
    const trimmed = (value || '').trim();
    if (!trimmed) return;
    const arr: string[] = this.editValues[field.path] || [];
    if (arr.includes(trimmed)) return;
    if (arr.length >= field.limit) return;
    arr.push(trimmed);
    this.editValues[field.path] = arr;
  }

  removeArrayValue(field: any, index: number) {
    const arr: any[] = this.editValues[field.path];
    if (arr) { arr.splice(index, 1); }
  }

  addSourceValue(field: any, event: { value: string, display: string }) {
    const arr: string[] = this.editValues[field.path] || [];
    const displayArr: any[] = this.displayValues[field.path] || [];
    if (arr.includes(event.value)) return;
    if (arr.length >= field.limit) return;
    arr.push(event.value);
    displayArr.push({ value: event.value, display: event.display });
    this.editValues[field.path] = arr;
    this.displayValues[field.path] = displayArr;
  }

  removeSourceValue(field: any, index: number) {
    const arr: any[] = this.editValues[field.path];
    const displayArr: any[] = this.displayValues[field.path];
    if (arr) { arr.splice(index, 1); }
    if (displayArr) { displayArr.splice(index, 1); }
  }

  onArrayStringKeydown(event: KeyboardEvent, field: any) {
    if (event.key === 'Enter') {
      event.preventDefault();
      const value = this.arrayInputValues[field.path] || '';
      this.addStringValue(field, value);
      this.arrayInputValues[field.path] = '';
    }
  }

  onArraySuggestionSelected(field: any, value: string) {
    this.addStringValue(field, value);
    this.arrayInputValues[field.path] = '';
  }

  addTelephoneValue(field: any, value: string) {
    const result = this.normalizeTelephone(value);
    if (result.error || !result.value) return;
    const arr: string[] = this.editValues[field.path] || [];
    if (arr.includes(result.value)) return;
    if (arr.length >= field.limit) return;
    arr.push(result.value);
    this.editValues[field.path] = arr;
  }

  onArrayTelephoneKeydown(event: KeyboardEvent, field: any) {
    if (event.key === 'Enter') {
      event.preventDefault();
      const value = this.arrayInputValues[field.path] || '';
      this.addTelephoneValue(field, value);
      this.arrayInputValues[field.path] = '';
    }
  }

  private normalizeTelephone(raw: any): { value: string | null, error: string | null } {
    if (raw === null || raw === undefined || raw === '') {
      return { value: null, error: null };
    }
    const digits = String(raw).replace(/\D/g, '');
    if (digits === '') return { value: null, error: null };
    if (digits.length === 10) return { value: '1' + digits, error: null };
    if (digits.length === 11) return { value: digits, error: null };
    return { value: null, error: 'must be a valid 10- or 11-digit telephone number.' };
  }

  async initializeUploader() {
    try {
      const uploader: any = await this.ioService.post('/file/requestUploader', {
        render: true,
        width: 300,
      });
      this.uploaderUrl = this.sanitizer.bypassSecurityTrustResourceUrl(uploader.uploader.iFrameSrc);
    } catch (e) {
      this.uploaderUrl = null;
    }
  }

  setActiveImageField(field: any) {
    this.activeImageField = this.activeImageField === field ? null : field;
    if (this.activeImageField && !this.uploaderUrl) {
      this.initializeUploader();
    }
  }

  getImageThumbnailUrl(url: string): string {
    if (!url) return '';
    return 'https://redfirefile.com/render?response=file&width=200&height=120&src=' + encodeURIComponent(url);
  }

  clearImageValue(field: any) {
    this.editValues[field.path] = null;
  }

  // A field offers a remote lookup when it carries a fully-configured remoteLookup.
  hasRemoteLookup(field: any): boolean {
    const r = field && field.remoteLookup;
    return !!(r && r.source && r.lookupKeyLocal && r.lookupKeyRemote && r.remoteFieldKey);
  }

  // Fetch this one field's value from its remote source and populate it. Leaves the
  // field untouched (and toasts) when nothing is found or the value is unchanged.
  async performRemoteLookup(field: any) {
    if (this.remoteLookupLoading[field.path]) return;
    this.remoteLookupLoading[field.path] = true;
    try {
      const result: any = await this.ioService.post('/view/remoteLookup', {
        viewKey: this.viewKey,
        documentId: this.documentId,
        fieldPath: field.path,
        documentValues: this.editValues,
        locationId: this.activeLocation?._id || null,
      });

      if (!result || !result.found || result.value == null || result.value === '') {
        AppComponent.showSnack('No data found for ' + field.name + '.', 'OK', 3000);
      } else if (result.value === this.editValues[field.path]) {
        AppComponent.showSnack(field.name + ' is already up to date.', 'OK', 3000);
      } else {
        this.editValues[field.path] = result.value;
        AppComponent.showSnack(field.name + ' updated.', 'OK', 3000);
      }
    } catch (e) {
      AppComponent.showSnack(e?.error?.msg || 'Remote lookup failed.', 'OK', 4000);
    } finally {
      this.remoteLookupLoading[field.path] = false;
    }
  }

  onImageDragOver(event: DragEvent, field: any) {
    event.preventDefault();
    event.stopPropagation();
    clearTimeout(this.dragLeaveTimer);
    this.dragOverField = field;
  }

  onImageDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.dragLeaveTimer = setTimeout(() => this.dragOverField = null, 50);
  }

  onImageDrop(event: DragEvent, field: any) {
    event.preventDefault();
    event.stopPropagation();
    clearTimeout(this.dragLeaveTimer);
    this.dragOverField = null;

    const files = event.dataTransfer?.files;
    if (!files || !files.length || !this.canUploadImages) return;
    this.uploadDroppedFiles(files, field);
  }

  async uploadDroppedFiles(files: FileList, field: any) {
    if (this.uploading) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic'];
    const validFiles = Array.from(files).filter(f => allowedTypes.includes(f.type));
    if (validFiles.length === 0) return;

    this.uploading = true;

    for (const file of validFiles) {
      if (this.isArrayField(field)) {
        const arr = this.editValues[field.path] || [];
        if (arr.length >= field.limit) break;
      }

      try {
        const uploader: any = await this.ioService.post('/file/requestUploader', {
          render: true,
          width: 300,
        });

        const result = await this.uploadFileToFileApi(
          file,
          uploader.uploader.key,
          uploader.uploader.directUploadUrl
        );

        if (result && result.url) {
          if (this.isArrayField(field)) {
            const arr: string[] = this.editValues[field.path] || [];
            arr.push(result.url);
            this.editValues[field.path] = arr;
          } else {
            this.editValues[field.path] = result.url;
            break;
          }
        }
      } catch (e) {
        console.log('Upload failed: ', e);
      }
    }

    this.uploading = false;
    this.initializeUploader();
  }

  private async uploadFileToFileApi(file: File, key: string, directUploadUrl: string): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('data', JSON.stringify({ key }));

    const response = await fetch(directUploadUrl, {
      method: 'POST',
      body: formData,
    });

    return response.json();
  }

  private resolveActiveFields(userActiveFields?: string[]) {
    const allFields = this.view.fields || [];
    const activePaths = userActiveFields || this.view.activeFields || null;
    if (activePaths && activePaths.length > 0) {
      const fieldMap: { [path: string]: any } = {};
      for (const f of allFields) { fieldMap[f.path] = f; }
      this.activeFields = activePaths.map(p => fieldMap[p]).filter(f => !!f);
    } else {
      this.activeFields = [...allFields];
    }
  }

  toggleFieldEditor() {
    this.showFieldEditor = !this.showFieldEditor;
    if (this.showFieldEditor) {
      this.buildEditorFields();
    }
  }

  private buildEditorFields() {
    const allFields = this.view.fields || [];
    const activePaths = this.activeFields.map(f => f.path);
    // Active fields first, in order
    this.editorFields = [];
    for (const f of this.activeFields) {
      this.editorFields.push({ field: f, active: true });
    }
    // Then inactive fields, in their original order
    for (const f of allFields) {
      if (!activePaths.includes(f.path)) {
        this.editorFields.push({ field: f, active: false });
      }
    }
  }

  toggleEditorField(item: { field: any, active: boolean }) {
    item.active = !item.active;
  }

  // Reorder a sortable array field's staged values (field.sortable on the View
  // doc). sourceCollection fields keep two index-aligned arrays — raw ids in
  // editValues and resolved names in displayValues — so both move together and
  // what saves matches what the user saw. The new order persists on Save like
  // any other staged edit.
  dropArrayValue(event: CdkDragDrop<any>, field: any) {
    if (event.previousIndex === event.currentIndex) {
      return;
    }
    const values = this.editValues[field.path];
    if (!Array.isArray(values)) {
      return;
    }
    moveItemInArray(values, event.previousIndex, event.currentIndex);
    const displays = this.displayValues[field.path];
    if (Array.isArray(displays)) {
      moveItemInArray(displays, event.previousIndex, event.currentIndex);
    }
  }

  dropEditorField(event: CdkDragDrop<any[]>) {
    moveItemInArray(this.editorFields, event.previousIndex, event.currentIndex);
  }

  async saveFieldSettings() {
    if (this.savingSettings) return;
    this.savingSettings = true;
    try {
      const activeFields = this.editorFields.filter(i => i.active).map(i => i.field.path);
      await this.ioService.post('/view/saveViewSettings', {
        viewKey: this.viewKey,
        activeFields,
      });
      this.showFieldEditor = false;
      AppComponent.showSnack('View settings saved.', 'OK', 3000);
      await this.loadView();
    } catch (e) {
      AppComponent.showSnack('Failed to save settings.', 'OK', 3000);
    }
    this.savingSettings = false;
  }

  async resetFieldSettings() {
    if (this.savingSettings) return;
    this.savingSettings = true;
    try {
      await this.ioService.post('/view/resetViewSettings', {
        viewKey: this.viewKey,
      });
      this.showFieldEditor = false;
      AppComponent.showSnack('View settings reset to defaults.', 'OK', 3000);
      await this.loadView();
    } catch (e) {
      AppComponent.showSnack('Failed to reset settings.', 'OK', 3000);
    }
    this.savingSettings = false;
  }

  private loadActions() {
    this.permittedActions = [];
    this.permittedActionsAboveSave = [];
    if (!this.view || !this.view.actions || !this.view.actions.length) return;
    for (const action of this.view.actions) {
      // Document-conditional actions: `whenPath` / `unlessPath` hide an action
      // that makes no sense for this record (e.g. Revoke on an already-revoked
      // device). Same semantics as the filter/count conditions.
      if (!this.passesDocCondition(action)) continue;
      // Unmanageable User record: only navigation/print actions remain, plus Set
      // PIN on the viewer's own record (the server allows self PIN resets).
      if (!this.targetManageable && action.type !== 'reportLink' && action.type !== 'print'
          && !(this.isSelfDocument && action.key === 'setPin')) {
        continue;
      }
      const target = action.aboveSave ? this.permittedActionsAboveSave : this.permittedActions;
      if (action.type === 'reportLink' || action.type === 'print') {
        // Navigation/print only — the target report enforces its own permission.
        target.push(action);
        continue;
      }
      if (action.serverGate) {
        // Server-resolved gate (e.g. per-ledger correction permission) — getView
        // returns the verdict in actionAllowed; static view.<key>.<action>
        // grants cannot express it.
        if (this.actionAllowed[action.key] === true) {
          target.push(action);
        }
        continue;
      }
      const permissionKey = 'view.' + this.viewKey + '.' + action.key;
      if (this.authorizationService.checkPermission(permissionKey, 'any', false)) {
        target.push(action);
      }
    }
  }

  // Rich text methods
  private initRichTextEditors() {
    const editors = document.querySelectorAll('.rt-editor[data-path]');
    editors.forEach((el: Element) => {
      const htmlEl = el as HTMLElement;
      const path = htmlEl.getAttribute('data-path');
      if (path && this.editValues[path]) {
        htmlEl.innerHTML = this.editValues[path];
      }
    });
  }

  onRtInput(event: Event, field: any) {
    this.editValues[field.path] = (event.target as HTMLElement).innerHTML;
  }

  onRtPaste(event: ClipboardEvent, field: any) {
    event.preventDefault();
    const text = event.clipboardData?.getData('text/plain');
    if (text) {
      document.execCommand('insertText', false, text);
    }
    this.updateRtState(field);
    this.syncRtValue(field);
  }

  rtExec(event: MouseEvent, field: any, command: string, value?: string) {
    event.preventDefault();
    document.execCommand(command, false, value || null);
    this.updateRtState(field);
    this.syncRtValue(field);
  }

  updateRtState(field: any) {
    this.rtState[field.path] = {
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      underline: document.queryCommandState('underline'),
      justifyLeft: document.queryCommandState('justifyLeft'),
      justifyCenter: document.queryCommandState('justifyCenter'),
      justifyRight: document.queryCommandState('justifyRight'),
    };
  }

  toggleRtDropdown(event: MouseEvent, field: any, dropdown: string) {
    event.preventDefault();
    event.stopPropagation();
    this.saveRtSelection(field);
    this.rtDropdown[field.path] = this.rtDropdown[field.path] === dropdown ? null : dropdown;
  }

  rtSetFontSize(event: MouseEvent, field: any, size: string) {
    event.preventDefault();
    this.restoreRtSelection(field);
    document.execCommand('fontSize', false, size);
    this.rtDropdown[field.path] = null;
    this.syncRtValue(field);
  }

  rtSetColor(event: MouseEvent, field: any, color: string) {
    event.preventDefault();
    this.restoreRtSelection(field);
    document.execCommand('foreColor', false, color);
    this.rtDropdown[field.path] = null;
    this.syncRtValue(field);
  }

  rtStartLink(event: MouseEvent, field: any) {
    event.preventDefault();
    this.saveRtSelection(field);
    this.rtLinkInput[field.path] = { active: true, url: '' };
    this.rtDropdown[field.path] = null;
  }

  rtApplyLink(field: any) {
    const input = this.rtLinkInput[field.path];
    if (!input || !input.url) return;
    this.restoreRtSelection(field);
    document.execCommand('createLink', false, input.url);
    const editor = document.querySelector('.rt-editor[data-path="' + field.path + '"]');
    if (editor) {
      editor.querySelectorAll('a').forEach(a => a.setAttribute('target', '_blank'));
    }
    this.rtLinkInput[field.path] = null;
    this.syncRtValue(field);
  }

  rtCancelLink(field: any) {
    this.rtLinkInput[field.path] = null;
    this.restoreRtSelection(field);
  }

  rtClearFormat(event: MouseEvent, field: any) {
    event.preventDefault();
    const editor = document.querySelector('.rt-editor[data-path="' + field.path + '"]') as HTMLElement;
    if (!editor) return;
    const selection = window.getSelection();
    const hasSelection = selection && selection.rangeCount > 0 && !selection.getRangeAt(0).collapsed
      && editor.contains(selection.anchorNode);
    if (!hasSelection) {
      const range = document.createRange();
      range.selectNodeContents(editor);
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
    document.execCommand('removeFormat', false);
    document.execCommand('unlink', false);
    document.execCommand('justifyLeft', false);
    this.updateRtState(field);
    this.syncRtValue(field);
  }

  rtClearValue(event: MouseEvent, field: any) {
    event.preventDefault();
    const editor = document.querySelector('.rt-editor[data-path="' + field.path + '"]') as HTMLElement;
    if (editor) editor.innerHTML = '';
    this.editValues[field.path] = null;
    this.rtLinkInput[field.path] = null;
    this.rtDropdown[field.path] = null;
    this.updateRtState(field);
  }

  getSafeHtml(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html || '');
  }

  private saveRtSelection(field: any) {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      this.rtSavedSelections[field.path] = sel.getRangeAt(0).cloneRange();
    }
  }

  private restoreRtSelection(field: any) {
    const range = this.rtSavedSelections[field.path];
    if (range) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    }
  }

  private syncRtValue(field: any) {
    const editor = document.querySelector('.rt-editor[data-path="' + field.path + '"]') as HTMLElement;
    if (editor) {
      this.editValues[field.path] = editor.innerHTML;
    }
  }

  getCalculatedValue(field: any): number | null {
    if (!field.formula) return null;
    const val = this.evaluateFormula(field.formula, new Set());
    this.calculatedValues[field.name] = val;
    return val;
  }

  formatCalculatedValue(value: number, format: string): string {
    if (value === null || value === undefined || isNaN(value)) return '\u2014';
    switch (format) {
      case 'currency': {
        const fixed = value.toFixed(2);
        const parts = fixed.split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        return '$' + parts.join('.');
      }
      case 'percent':
        return value.toFixed(2) + '%';
      default:
        return String(value);
    }
  }

  private evaluateFormula(formula: string, visited: Set<string>): number | null {
    // Path refs may be dotted (segments start with a letter/underscore so a
    // trailing decimal like "$a * 1.5" is never swallowed into the path).
    const resolved = formula.replace(/\$([a-zA-Z_][a-zA-Z0-9_]*(?:\.[a-zA-Z_][a-zA-Z0-9_]*)*)/g, (_match, path) => {
      const refField = (this.view?.fields || []).find((f: any) => f.path === path);
      if (!refField) return 'NaN';

      if (refField.type === 'calculated') {
        if (visited.has(path)) return 'NaN';
        visited.add(path);
        const val = this.evaluateFormula(refField.formula, visited);
        return val !== null ? val.toString() : 'NaN';
      }

      const raw = this.editValues[path];
      if (raw == null || raw === '') return 'NaN';
      const num = parseFloat(raw);
      return isNaN(num) ? 'NaN' : num.toString();
    });

    return this.safeEvalMath(resolved);
  }

  private safeEvalMath(expr: string): number | null {
    const tokens: string[] = [];
    let i = 0;
    while (i < expr.length) {
      if (expr[i] === ' ') { i++; continue; }
      if ('+-*/()'.includes(expr[i])) {
        tokens.push(expr[i]);
        i++;
      } else if (/[0-9.]/.test(expr[i])) {
        let num = '';
        while (i < expr.length && /[0-9.]/.test(expr[i])) { num += expr[i]; i++; }
        tokens.push(num);
      } else {
        return null;
      }
    }

    let pos = 0;
    const peek = () => tokens[pos];
    const consume = () => tokens[pos++];

    const parseExpr = (): number | null => {
      let left = parseTerm();
      if (left === null) return null;
      while (peek() === '+' || peek() === '-') {
        const op = consume();
        const right = parseTerm();
        if (right === null) return null;
        left = op === '+' ? left + right : left - right;
      }
      return left;
    };

    const parseTerm = (): number | null => {
      let left = parseFactor();
      if (left === null) return null;
      while (peek() === '*' || peek() === '/') {
        const op = consume();
        const right = parseFactor();
        if (right === null) return null;
        if (op === '/') {
          if (right === 0) return null;
          left = left / right;
        } else {
          left = left * right;
        }
      }
      return left;
    };

    const parseFactor = (): number | null => {
      if (peek() === '(') {
        consume();
        const val = parseExpr();
        if (peek() !== ')') return null;
        consume();
        return val;
      }
      if (peek() === '-') {
        consume();
        const val = parseFactor();
        return val !== null ? -val : null;
      }
      const token = consume();
      if (token === undefined) return null;
      const num = parseFloat(token);
      return isNaN(num) ? null : num;
    };

    const result = parseExpr();
    if (pos !== tokens.length) return null;
    if (result === null || !isFinite(result)) return null;
    return result;
  }

  // Leaving for an action's editor (dialog or route) comes back to a reloaded
  // record, which would wipe unsaved field edits — offer to save them first.
  // Resolves false when the user cancels or the save fails.
  private async saveUnsavedChangesBeforeAction(): Promise<boolean> {
    if (!this.canUpdate || !this.hasUnsavedChanges()) {
      return true;
    }
    const confirmRef = AppComponent.showMessage(
      'Unsaved Changes',
      `Save your changes to ${this.document?.name || 'this record'} first?`,
      ['Save & Continue', 'Cancel'],
      false,
    );
    const confirmResult: DialogOption = await confirmRef.afterClosed().toPromise();
    if (!confirmResult || confirmResult.value !== 'Save & Continue') {
      return false;
    }
    return this.save(false);
  }

  async executeAction(action: any) {
    if (this.executingAction) return;
    if (action.type === 'component') {
      // Open a custom editor component in a dialog, then reload the record so
      // any values the editor changed (e.g. calculated costs) show fresh.
      const componentClass = ACTION_COMPONENTS[action.component];
      if (!componentClass) {
        this.errors = ['Component "' + action.component + '" is not registered.'];
        return;
      }
      if (!await this.saveUnsavedChangesBeforeAction()) {
        return;
      }
      // The dialog opens via the ?dialogAction param (see syncComponentDialog)
      // so its open state lives in browser history.
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { dialogAction: action.key },
        queryParamsHandling: 'merge',
      });
      return;
    }
    if (action.type === 'route') {
      // Navigate to a child page of this record (e.g. details/.../vendors)
      // that replaces the detail panel full-screen; browser Back returns here.
      if (!await this.saveUnsavedChangesBeforeAction()) {
        return;
      }
      this.router.navigate([action.route], {
        relativeTo: this.route,
        queryParamsHandling: 'preserve',
      });
      return;
    }
    if (action.type === 'reportLink') {
      // Resolve $-refs on every filter field (value, displayValue, …) against the
      // document, so e.g. an id filter can carry its display name alongside the id.
      // Filters may be gated by whenPath/unlessPath so a single action can target a
      // different field depending on the document (e.g. categoryId vs subcategoryId).
      const filters = (action.filters || [])
        .filter(filter => this.passesDocCondition(filter))
        .map(filter => {
          const resolved = this.resolveDataRefs(filter);
          delete resolved.whenPath;
          delete resolved.unlessPath;
          return resolved;
        });
      this.reportService.loadFilteredReport(action.reportKey, filters);
      return;
    }
    if (action.type === 'print') {
      // Render just this record through the report's print template and open the
      // browser print dialog. The report enforces its own authorization server-side.
      const input: any = {
        key: action.reportKey,
        templateKey: action.templateKey,
        clientConfig: {
          download: true,
          limit: 0,
          clientFilters: [{ path: 'documentId', type: 'id', value: [this.documentId] }],
        },
      };
      try {
        await this.tagPrintService.print('report/loadRecords', input, { description: action.name });
      } catch (e) {
        this.errors = [e?.error?.msg || 'Print failed.'];
      }
      return;
    }
    if (action.type === 'update') {
      // Confirm-gated server-side recompute (e.g. re-pull derived values), then
      // reload the record in place so the refreshed values show without closing.
      const confirmRef = AppComponent.showMessage(
        'Confirm',
        action.confirm || `${action.name}? This re-pulls calculated values from current data; your manual entries are kept.`,
        [action.name || 'Update', 'Cancel'],
        false,
      );
      confirmRef.afterClosed().subscribe(async (result: DialogOption) => {
        if (!result || result.value === 'Cancel') return;
        this.executingAction = true;
        this.errors = [];
        try {
          const data: any = {};
          for (const key of Object.keys(action.data || {})) {
            const val = action.data[key];
            data[key] = (typeof val === 'string' && val.startsWith('$')) ? this.document[val.substring(1)] : val;
          }
          const response: any = await this.ioService.post(action.route, data);
          if (action.responseKey && response && response[action.responseKey] != null) {
            // Surface a value the route generated (e.g. Set PIN returns the new
            // PIN) — a snack would vanish before the operator can read it.
            AppComponent.showMessage(
              action.name || 'Update',
              (action.responseLabel ? action.responseLabel + ': ' : '') + response[action.responseKey],
              ['OK'],
              false,
            );
          } else {
            AppComponent.showSnack((action.name || 'Update') + ' complete.', 'OK', 3000);
          }
          await this.loadView();
        } catch (e) {
          this.errors = [e?.error?.msg || (action.name || 'Update') + ' failed.'];
        }
        this.executingAction = false;
      });
      return;
    }
    const docName = this.document?.name || '';
    const dialogRef = AppComponent.showMessage('Confirm', `Are you sure you want to ${action.name} ${docName}?`, ['Confirm', 'Cancel'], false);
    dialogRef.afterClosed().subscribe(async (result: DialogOption) => {
      if (!result || result.value !== 'Confirm') return;
      this.executingAction = true;
      this.errors = [];
      try {
        if (action.route) {
          // Purpose-built action: post the resolved data to the action's own route.
          const data: any = {};
          for (const key of Object.keys(action.data || {})) {
            const val = action.data[key];
            if (typeof val === 'string' && val.startsWith('$')) {
              data[key] = this.document[val.substring(1)];
            } else {
              data[key] = val;
            }
          }
          await this.ioService.post(action.route, data);
        } else if (action.key === 'delete') {
          // Generic delete: no route defined, so use Cloud API's generic soft-delete.
          await this.ioService.post('/view/deleteRecord', {
            viewKey: this.viewKey,
            documentId: this.documentId,
          });
        } else {
          throw { error: { msg: 'This action is not configured.' } };
        }
        AppComponent.showSnack(action.name + ' completed.', 'OK', 3000);
        this.reportService.loadReport(null);
        this.close();
      } catch (e) {
        this.errors = [e?.error?.msg || 'Action failed.'];
        this.executingAction = false;
      }
    });
  }

}
